
import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate } from 'react-router-dom';
import { Auth } from '@supabase/auth-ui-react';
import { ThemeSupa } from '@supabase/auth-ui-shared';
import { useUser, UserProvider } from './context/UserContext';
import { supabase } from './integrations/supabase/client';
import Layout from './components/Layout';
import IntegratedDashboard from './pages/IntegratedDashboard';
import Dashboard from './pages/Dashboard';
import SymptomReporting from './pages/SymptomReporting';
import Leaderboard from './pages/Leaderboard';
import CycleTracking from './pages/CycleTracking';
import CycleImport from './pages/CycleImport';
import HomePage from './pages/HomePage';
import Profile from './pages/Profile';
import Admin from './pages/Admin';
import AdminRoute from './components/AdminRoute';
import PublicRoute from './components/PublicRoute';
import PrivateRoute from './components/PrivateRoute';
import ComingSoon from './pages/ComingSoon';
import Guide from './pages/Guide';
import Lesson from './pages/Lesson';
import Calendar from './pages/Calendar';
import NotFound from './pages/NotFound';
import Login from './pages/Login'; // Import the Login component

// Resource pages
import Search from './pages/resources/Search';
import FAQ from './pages/resources/FAQ';
import ContactUs from './pages/resources/ContactUs';
import ForCouples from './pages/resources/ForCouples';
import PrivacyPolicy from './pages/resources/PrivacyPolicy';
import RefundPolicy from './pages/resources/RefundPolicy';
import TermsOfService from './pages/resources/TermsOfService';

// Create placeholder component for Waitlist
const Waitlist = () => <div>Waitlist</div>;

const AppContent = () => {
  const { user, isLoading, isAuthenticated } = useUser();
  
  if (isLoading) {
    return <div>Loading...</div>;
  }
  
  return (
    <Routes>
      {/* Public Routes - Use PublicRoute correctly */}
      <Route path="/waitlist" element={<PublicRoute><Waitlist /></PublicRoute>} />
      <Route path="/" element={<HomePage />} /> {/* HomePage does its own check, no need for PublicRoute */}
      <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} /> {/* Add Login route */}
      
      {/* Auth UI Route - accessible when not logged in */}
      <Route
        path="/auth"
        element={
          user ? (
            <Navigate to="/integrated-dashboard" replace />
          ) : (
            <div className="container mx-auto py-8">
              <Auth
                supabaseClient={supabase}
                appearance={{ theme: ThemeSupa }}
                providers={['google', 'github']}
                redirectTo={`${window.location.origin}/integrated-dashboard`}
              />
            </div>
          )
        }
      />
      
      {/* Private Routes - accessible only when logged in */}
      <Route path="/integrated-dashboard" element={<PrivateRoute><IntegratedDashboard /></PrivateRoute>} />
      <Route path="/dashboard" element={<PrivateRoute><Dashboard /></PrivateRoute>} />
      <Route path="/profile" element={<PrivateRoute><Profile /></PrivateRoute>} />
      <Route path="/symptom-reporting" element={<PrivateRoute><SymptomReporting /></PrivateRoute>} />
      <Route path="/leaderboard" element={<PrivateRoute><Leaderboard /></PrivateRoute>} />
      <Route path="/guide" element={<PrivateRoute><Guide /></PrivateRoute>} />
      <Route path="/calendar" element={<PrivateRoute><Calendar /></PrivateRoute>} />
      <Route path="/lesson/:day" element={<PrivateRoute><Lesson /></PrivateRoute>} />
      
      {/* Resource Pages */}
      <Route path="/search" element={<Search />} />
      <Route path="/faq" element={<FAQ />} />
      <Route path="/contact" element={<ContactUs />} />
      <Route path="/for-couples" element={<ForCouples />} />
      <Route path="/privacy-policy" element={<PrivacyPolicy />} />
      <Route path="/refund-policy" element={<RefundPolicy />} />
      <Route path="/terms-of-service" element={<TermsOfService />} />
      
      {/* Coming Soon Routes */}
      <Route path="/community" element={<PrivateRoute><ComingSoon /></PrivateRoute>} />
      
      {/* Admin Routes - accessible only when logged in AND user is an admin */}
      <Route path="/admin" element={<AdminRoute><Admin /></AdminRoute>} />

      {/* Cycle Tracking */}
      <Route path="/cycle-tracking" element={<PrivateRoute><CycleTracking /></PrivateRoute>} />
      <Route path="/cycle-import" element={<PrivateRoute><CycleImport /></PrivateRoute>} />
      
      {/* 404 Not Found Page */}
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
};

const App: React.FC = () => {
  return (
    <Router>
      <UserProvider>
        <AppContent />
      </UserProvider>
    </Router>
  );
};

export default App;
